import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PromoBanner from "../../components/PromoBanner";
import { Trophy, Users, Calendar, Star, TrendingUp } from "lucide-react";

const BADGE_LABELS = {
  verified_trader: "Verified Trader",
  top_contributor: "Top Contributor",
  helpful: "Helpful",
  educator: "Educator",
  early_member: "Early Member",
  market_wizard: "Market Wizard",
  momentum_trader: "Momentum Trader",
  swing_trader: "Swing Trader",
  long_term_investor: "Long-term Investor",
  crypto_expert: "Crypto Expert",
  options_expert: "Options Expert",
  moderator: "Moderator",
  founder: "Founder",
  flowfinder_team: "FlowFinder Team",
  streak_7: "7-day Streak",
  streak_30: "30-day Streak",
  streak_90: "90-day Streak",
  mvp_week: "MVP of the Week",
  mvp_month: "MVP of the Month",
};

export default function UserProfile() {
  const { username } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/api/community/profile/${username}`).then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [username]);

  if (!data) return (
    <div className="min-h-screen flex flex-col">
      <Header /><PromoBanner />
      <main id="main-content" className="flex-1 max-w-[900px] w-full mx-auto px-4 sm:px-6 py-6">
        <div className="text-white/50 text-sm text-center py-10">Loading…</div>
      </main>
      <Footer />
    </div>
  );

  if (data.error) return (
    <div className="min-h-screen flex flex-col">
      <Header /><PromoBanner />
      <main id="main-content" className="flex-1 max-w-[900px] w-full mx-auto px-4 sm:px-6 py-6">
        <div className="text-white/50 text-sm text-center py-10">User not found</div>
      </main>
      <Footer />
    </div>
  );

  const p = data.profile;
  return (
    <div className="min-h-screen flex flex-col" data-testid="profile-page">
      <Header />
      <PromoBanner />
      <main id="main-content" className="flex-1 max-w-[900px] w-full mx-auto px-4 sm:px-6 py-6">
        <div className="card-surface p-5 mb-3 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-40"
               style={{ background: "radial-gradient(circle at 100% 0%, rgba(0,255,156,0.14), transparent 60%)" }} />
          <div className="relative flex items-start gap-4">
            {p.avatar_url && (
              <img src={p.avatar_url} alt="" className="w-20 h-20 rounded-2xl bg-white/5" data-testid="profile-avatar"/>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-white text-xl font-semibold tracking-tight" data-testid="profile-username">
                @{p.username}
              </div>
              {p.display_name && p.display_name !== p.username && (
                <div className="text-white/60 text-[13px] mt-0.5">{p.display_name}</div>
              )}
              {p.bio && <p className="text-white/80 text-[13px] mt-2 max-w-xl">{p.bio}</p>}
              <div className="mt-3 flex items-center gap-4 label-mono">
                <span><Trophy className="inline w-3 h-3 mr-1 text-[#ffd166]" />{p.karma || 0} karma</span>
                <span><Users className="inline w-3 h-3 mr-1" />{p.followers || 0} followers</span>
                <span><Calendar className="inline w-3 h-3 mr-1" />joined {new Date(p.join_date).toLocaleDateString()}</span>
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {p.trading_style && (
                  <span className="px-2 py-0.5 rounded-full bg-[var(--bull)]/15 text-[var(--bull)] text-[11px] font-mono uppercase tracking-wider border border-[var(--bull)]/25">
                    {p.trading_style.replace(/_/g, " ")}
                  </span>
                )}
                {p.experience_level && (
                  <span className="px-2 py-0.5 rounded-full bg-white/[0.04] text-white/70 text-[11px] font-mono uppercase tracking-wider border border-white/10">
                    {p.experience_level}
                  </span>
                )}
                {(p.badges || []).map((b) => (
                  <span key={`badge-${b}`}
                        className="px-2 py-0.5 rounded-full bg-[#ffd166]/15 text-[#ffd166] text-[11px] font-mono border border-[#ffd166]/30">
                    <Star className="inline w-3 h-3 mr-1" />
                    {BADGE_LABELS[b] || b}
                  </span>
                ))}
              </div>
            </div>
          </div>
          {p.favorite_tickers?.length > 0 && (
            <div className="relative mt-4">
              <div className="label-mono mb-1.5">Favorite tickers</div>
              <div className="flex flex-wrap gap-1.5">
                {p.favorite_tickers.map((t) => (
                  <span key={`fav-${t}`}
                        className="px-2 py-0.5 rounded-full bg-white/[0.04] text-white/85 text-[11px] font-mono border border-white/10">
                    ${t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="label-mono mb-2">Recent posts</div>
        <div className="space-y-2">
          {(data.recent_posts || []).length === 0 && (
            <div className="text-white/40 text-sm text-center py-8">No posts yet</div>
          )}
          {(data.recent_posts || []).map((post) => (
            <Link key={post.id} to={`/community/post/${post.id}`}
                  className="card-surface p-3 block hover:border-white/20 transition-colors"
                  data-testid={`profile-post-${post.id}`}>
              <div className="flex items-center gap-2 label-mono">
                <span>r/{post.community_slug}</span>
                <span>·</span>
                <span>{new Date(post.created_at).toLocaleDateString()}</span>
                {(post.ticker_tags || []).slice(0, 3).map((t) => (
                  <span key={`ppost-t-${post.id}-${t}`}
                        className="px-1.5 py-0.5 rounded bg-[var(--bull)]/15 text-[var(--bull)] font-mono text-[10px]">
                    ${t}
                  </span>
                ))}
              </div>
              <div className="mt-1 text-white text-[14px] font-semibold tracking-tight">{post.title}</div>
              <div className="mt-1 label-mono">
                ↑ {post.score} · 💬 {post.comment_count}
              </div>
            </Link>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
